import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-white/20 rounded-lg transition"
            title="Back to home"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
            <p className="text-purple-100">RepuGate - AI-Powered Facebook Review Manager</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-slate-800/50 backdrop-blur-xl border border-purple-500/20 rounded-2xl p-8 text-slate-100 space-y-8">
          
          {/* Last Updated */}
          <div className="text-sm text-purple-300 border-l-4 border-purple-500 pl-4">
            <strong>Last updated:</strong> April 1, 2026
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="text-slate-300 leading-relaxed">
              RepuGate ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            <div className="space-y-4 text-slate-300">
              <div>
                <h3 className="font-semibold text-white mb-2">2.1 Personal Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Facebook Profile Information (name, email, profile picture)</li>
                  <li>Facebook Page Information (page name, ID, access tokens)</li>
                  <li>Review Data (customer reviews, ratings, and responses)</li>
                  <li>Generated Replies and AI-generated content you create</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">2.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Browser type and version</li>
                  <li>IP address</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring/exit pages</li>
                  <li>Operating system</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-2">
              <li>Provide, operate, and maintain our services</li>
              <li>Generate AI-powered replies to customer reviews</li>
              <li>Post responses to Facebook on your behalf</li>
              <li>Store and manage review data for your reference</li>
              <li>Improve and optimize our service</li>
              <li>Communicate with you about service updates</li>
              <li>Prevent fraud and ensure security</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Facebook Integration</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              RepuGate integrates with Facebook Graph API to access your reviews and post responses. When you login with Facebook:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-2">
              <li>You grant us permission to read reviews posted on your Page</li>
              <li>We obtain access tokens to post comments/replies on your behalf</li>
              <li>Your data is stored securely in our Supabase database</li>
              <li>You can revoke access at any time through Facebook Settings</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. AI Services</h2>
            <p className="text-slate-300 leading-relaxed">
              RepuGate uses Groq AI services to generate reply suggestions for customer reviews. Review text is sent to Groq's API to generate appropriate responses. These interactions are subject to Groq's privacy policy and terms of service.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Storage & Security</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Your data is stored using Supabase (a PostgreSQL-based backend service). We implement:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-2">
              <li>Encrypted connections (HTTPS/TLS)</li>
              <li>Secure password authentication</li>
              <li>Regular security updates</li>
              <li>Access controls and data isolation</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              However, no method of transmission over the internet is 100% secure. Please report any security vulnerabilities responsibly.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Data Retention</h2>
            <p className="text-slate-300 leading-relaxed">
              We retain your Facebook review data and generated replies as long as you use the service. You can request data deletion at any time. Upon deletion, your data will be permanently removed from our database.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Third-Party Services</h2>
            <p className="text-slate-300 leading-relaxed">
              RepuGate uses the following third-party services:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-2">
              <li><strong>Facebook Graph API:</strong> For reading reviews and posting responses</li>
              <li><strong>Groq AI API:</strong> For generating reply suggestions</li>
              <li><strong>Supabase:</strong> For data storage and authentication</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              These services have their own privacy policies. We recommend reviewing them.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Your Rights</h2>
            <p className="text-slate-300 leading-relaxed">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of communications</li>
              <li>Revoke Facebook app permissions at any time</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Contact Us</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-2">
              <p className="text-slate-200"><strong>Email:</strong> mostakim.rubaiyat@yahoo.com</p>
              <p className="text-slate-200"><strong>Website:</strong> https://repugate.vercel.app</p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date above.
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-purple-500/20 pt-8 mt-8">
            <p className="text-sm text-slate-400 text-center">
              © 2026 RepuGate. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
